import { FilterRowSelection, RelationalOperator } from '../../types/filter';

export type AggregationType = 'count';

export interface AggregationDefinition {
  type: AggregationType;
  field?: string;
}

export type DatabaseMetadata = {
  columnName: string;
  dataType: 'string' | 'number' | 'date' | 'boolean';
};

export type CsvMetadata = {
  fieldType: 'database' | 'limit' | 'static';
  limitField?: string;
  columnOrder: number;
  columnName: string;
  dateFormat?: string;
  valueMapping?: { [key: string | number]: string };
};

export type StaffSeedMetadata = {
  dbColumnName?: string;
  staticValue?: string;
};

export enum ControlType {
  TEXT = 'text',
  DATE = 'date',
  DATE_RANGE = 'date-range',
  SELECT = 'select',
  MULTI_SELECT = 'multi-select',
  SEARCHABLE_MULTI_SELECT = 'searchable-multi-select',
  BOOLEAN_TOGGLE = 'boolean-toggle',
}

/**
 * Fields that are common to both the input data and the fully realized filter definition.
 * Document each field here so that both FilterDefinitionInput and FilterDefinition
 * inherit these descriptions.
 */
export interface FilterDefinitionFields {
  /**
   * Version of the filter definition schema.
   */
  version?: string;

  /**
   * Label displayed to the user for this filter field.
   */
  label?: string;

  /**
   * Tooltip displayed to guide users about this filter field.
   */
  toolTip?: string;

  /**
   * The type of UI control used for selecting values.
   */
  controlType?: ControlType;

  /**
   * Hide this field from the user interface.
   */
  isAdvancedFilter?: boolean;

  /**
   * Date format string (e.g. 'YYYY-MM-DD') when the fieldType is 'date'.
   */
  dateFormat?: string;

  /**
   * Operators available for filtering on this field.
   */
  availableOperators?: RelationalOperator[];

  /**
   * Metadata about the corresponding database column.
   */
  databaseMetadata?: DatabaseMetadata;

  /**
   * Metadata related to CSV export (column ordering, formatting, etc.).
   */
  csvMetadata: CsvMetadata;

  /**
   * Metadata used for seeding staff data from a static or database source.
   */
  staffSeedMetadata?: StaffSeedMetadata;

  /**
   * A mapping of raw data values to more user-friendly labels.
   */
  labelMapping?: Record<string, string>;

  /**
   * A set of default selectable options shown to the user.
   */
  defaultOptions?: FilterRowSelection['value'];

  /**
   * A default pre-selected value for the filter field.
   */
  defaultValue?: FilterRowSelection['value'];

  /**
   * The default relational operator to use if one is not specified.
   */
  defaultRelationalOperator?: RelationalOperator;

  /**
   * A custom sorting function for the values displayed to the user.
   */
  valueSortFn?: (value: (string | null)[]) => (string | null)[];

  /**
   * Indicates if this field is used for grouping in aggregation.
   */
  isGroupBy?: boolean;

  /**
   * Defines aggregation details (e.g. count) for this field.
   */
  aggregation?: AggregationDefinition;

  /**
   * Indicates if this field contains an array of values (e.g. flags) where individual
   * items may overlap across records, requiring special count handling.
   */
  isArrayField?: boolean;
}
